// Tree geometry (GEN-8). Pure builders that turn a rooted position + rng into a
// list of WORLD-space voxels. The feature pipeline (features.ts) stamps them,
// clipping to whichever chunk it is generating — so a canopy that straddles a
// chunk border is produced identically by both chunks (the "planner-lite margin"
// technique): every builder consumes the SAME rng regardless of clipping, because
// it builds the whole tree first and the caller clips afterwards.
import type { Rng } from '../../engine/rng';
import type { TreeRule } from '../../registry/schemas';

export type TreeKind = 'oak' | 'birch' | 'spruce' | 'cherry';

/** One block a tree wants to place, in WORLD coordinates. `leaf` cells only
 *  overwrite air; trunk cells (leaf=false) form the load-bearing log column. */
export interface TreeVoxel {
  x: number;
  y: number;
  z: number;
  leaf: boolean;
}

/** Block names each kind uses for its trunk (a vertical log) and its canopy. */
export const TREE_BLOCKS: Record<TreeKind, { log: string; leaves: string }> = {
  oak: { log: 'oak_log', leaves: 'oak_leaves' },
  birch: { log: 'birch_log', leaves: 'birch_leaves' },
  spruce: { log: 'spruce_log', leaves: 'spruce_leaves' },
  cherry: { log: 'cherry_log', leaves: 'cherry_leaves' },
};

/** Weighted pick from a rule's `types` (exactly one rng draw). */
export function pickKind(types: TreeRule['types'], rng: Rng): TreeKind {
  let total = 0;
  for (const t of types) total += t.weight;
  let r = rng.nextFloat() * total;
  for (const t of types) {
    r -= t.weight;
    if (r <= 0) return t.kind;
  }
  return types[0]!.kind;
}

// A flat square (Chebyshev-radius) layer of leaves; wide layers may trim corners.
function leafLayer(out: TreeVoxel[], cx: number, y: number, cz: number, r: number, trimCorners: boolean, rng: Rng): void {
  for (let dx = -r; dx <= r; dx++) {
    for (let dz = -r; dz <= r; dz++) {
      const corner = Math.abs(dx) === r && Math.abs(dz) === r;
      if (corner && trimCorners && rng.nextFloat() < 0.5) continue; // rounder look
      out.push({ x: cx + dx, y, z: cz + dz, leaf: true });
    }
  }
}

/** Build a tree rooted so its trunk base sits at `groundY + 1`. Consumes rng in a
 *  fixed order (height, then per-layer corner trims) so it is clip-independent. */
export function buildTree(kind: TreeKind, wx: number, wz: number, groundY: number, rng: Rng): TreeVoxel[] {
  const voxels: TreeVoxel[] = [];
  const baseY = groundY + 1;

  if (kind === 'spruce') {
    const h = 6 + rng.nextInt(4); // 6..9
    const trunkTop = baseY + h - 1;
    for (let y = baseY; y <= trunkTop; y++) voxels.push({ x: wx, y, z: wz, leaf: false });
    // Tapered conifer canopy — wide near the base, pointed on top.
    const canopyBase = groundY + 2 + rng.nextInt(2); // 2..3 above ground
    for (let y = canopyBase; y <= trunkTop; y++) {
      const d = trunkTop - y; // 0 at the very top
      const r = Math.min(2, Math.floor(d / 2));
      leafLayer(voxels, wx, y, wz, r, r === 2, rng);
    }
    voxels.push({ x: wx, y: trunkTop + 1, z: wz, leaf: true }); // point
    return voxels;
  }

  if (kind === 'cherry') {
    // A short trunk under a broad, rounded blossom canopy (wider than oak, so a
    // cherry grove reads as a sea of pink).
    const h = 5 + rng.nextInt(3); // 5..7
    const trunkTop = baseY + h - 1;
    for (let y = baseY; y <= trunkTop; y++) voxels.push({ x: wx, y, z: wz, leaf: false });
    leafLayer(voxels, wx, trunkTop - 1, wz, 3, true, rng);
    leafLayer(voxels, wx, trunkTop, wz, 3, true, rng);
    leafLayer(voxels, wx, trunkTop + 1, wz, 2, true, rng);
    leafLayer(voxels, wx, trunkTop + 2, wz, 1, false, rng);
    return voxels;
  }

  // oak / birch — a compact broadleaf canopy over the top four levels.
  const h = kind === 'birch' ? 5 + rng.nextInt(3) : 4 + rng.nextInt(3); // birch 5..7, oak 4..6
  const trunkTop = baseY + h - 1;
  for (let y = baseY; y <= trunkTop; y++) voxels.push({ x: wx, y, z: wz, leaf: false });
  leafLayer(voxels, wx, trunkTop - 2, wz, 2, true, rng);
  leafLayer(voxels, wx, trunkTop - 1, wz, 2, true, rng);
  leafLayer(voxels, wx, trunkTop, wz, 1, false, rng);
  leafLayer(voxels, wx, trunkTop + 1, wz, 1, true, rng);
  return voxels;
}
